import { z } from 'zod'
import type { AreEquivalent } from '../../types.ts'
import { BucketSwatchKeySchema } from './resolution-categorization-palette.ts'
import {
  CategorizationPresetSchema,
  getDefaultCategorization,
  type Categorization,
} from './resolution-categorization.ts'
import { EMPTY_RESOLUTION_SCOPE, type ResolutionScope } from './resolution-scope.ts'
import { ResolutionStateSchema } from './resolution-taxonomy.ts'

const STORAGE_KEY = 'resolution-panel-expanded'

/**
 * Whether the panel was last left expanded. The design opens collapsed by default, so an absent
 * or unreadable value means collapsed. Reads are guarded because Safari's private mode throws on
 * localStorage access rather than returning null.
 */
export const getIsResolutionPanelExpanded = (): boolean => {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'true'
  } catch {
    return false
  }
}

export const setIsResolutionPanelExpanded = (isExpanded: boolean): void => {
  try {
    localStorage.setItem(STORAGE_KEY, String(isExpanded))
  } catch {
    // A viewer who blocks site data still gets a working panel, just not a remembered state.
  }
}

const SCOPE_STORAGE_KEY = 'resolution-panel-scope'
export const CATEGORIZATION_STORAGE_KEY = 'resolution-panel-categorization'

/**
 * Reads a stored preference, falling back when it is absent, unreadable, or not the shape it was
 * written in.
 *
 * The parse is guarded as well as the read: what comes back is whatever was last written under this
 * key, which a different build — or a hand-edited entry — can have left in another shape. A
 * malformed value returns the fallback rather than propagating into the panel.
 */
const readStoredJson = <TValue>({
  key,
  isValid,
  fallback,
}: {
  key: string
  isValid: (parsed: unknown) => parsed is TValue
  fallback: TValue
}): TValue => {
  try {
    const stored = localStorage.getItem(key)
    if (!stored) {
      return fallback
    }
    const parsed: unknown = JSON.parse(stored)

    return isValid(parsed) ? parsed : fallback
  } catch {
    return fallback
  }
}

const writeStoredJson = (key: string, value: unknown): void => {
  try {
    localStorage.setItem(key, JSON.stringify(value))
  } catch {
    // A viewer who blocks site data still gets a working panel, just not a remembered one.
  }
}

const isStringArray = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every(entry => typeof entry === 'string')

const isResolutionScope = (parsed: unknown): parsed is ResolutionScope =>
  typeof parsed === 'object' &&
  parsed !== null &&
  'triageOutcomes' in parsed &&
  'fixOutcomes' in parsed &&
  'severityLabels' in parsed &&
  isStringArray(parsed.triageOutcomes) &&
  isStringArray(parsed.fixOutcomes) &&
  isStringArray(parsed.severityLabels)

export const getStoredResolutionScope = (): ResolutionScope =>
  readStoredJson({ key: SCOPE_STORAGE_KEY, isValid: isResolutionScope, fallback: EMPTY_RESOLUTION_SCOPE })

export const setStoredResolutionScope = (scope: ResolutionScope): void => writeStoredJson(SCOPE_STORAGE_KEY, scope)

/**
 * A bucket stores its swatch key, not a CSS value. An entry written before that — a bucket carrying
 * a resolved `color` and no `swatch` — fails the parse, so it falls back to the default
 * categorization instead of drawing buckets in colours the palette no longer offers.
 */
const StoredCategorizationSchema = z.object({
  presetKey: CategorizationPresetSchema,
  buckets: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      swatch: BucketSwatchKeySchema,
      states: z.array(ResolutionStateSchema),
    })
  ),
})

true satisfies AreEquivalent<z.infer<typeof StoredCategorizationSchema>, Categorization>

const isCategorization = (parsed: unknown): parsed is Categorization =>
  StoredCategorizationSchema.safeParse(parsed).success

export const getStoredCategorization = (): Categorization =>
  readStoredJson({
    key: CATEGORIZATION_STORAGE_KEY,
    isValid: isCategorization,
    fallback: getDefaultCategorization(),
  })

export const setStoredCategorization = (categorization: Categorization): void =>
  writeStoredJson(CATEGORIZATION_STORAGE_KEY, categorization)
